<script setup lang="ts">
import { InfoTip } from '@it-tools/shell'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormat } from '../composables/useFormat'
import type { CostShares, NodeFit, SensitivityPoint } from '../model/fit'
import type { Segment } from './types'
import SensitivityChart from './SensitivityChart.vue'
import StackedBar from './StackedBar.vue'

const props = defineProps<{
  fit: NodeFit
  points: readonly SensitivityPoint[]
  step: number
  hours: number
}>()

const emit = defineEmits<{ 'update:step': [number] }>()

const { t } = useI18n()
const format = useFormat()

// Same hues as the node diagram; eviction is folded into "system" here because it
// is money spent on keeping the node alive, like the reservations.
const SHARES = [
  { key: 'pods', color: '--color-chart-1' },
  { key: 'daemonsets', color: '--color-chart-2' },
  { key: 'system', color: '--color-chart-3' },
  { key: 'unused', color: 'none' },
] as const satisfies readonly { key: keyof CostShares; color: string }[]

const instanceCost = computed(() => props.fit.instance.hourlyPrice * props.hours)

const amount = (key: keyof CostShares): number => props.fit.costShares[key] * instanceCost.value

const segments = computed<Segment[]>(() =>
  SHARES.map((share) => ({
    key: share.key,
    value: props.fit.costShares[share.key],
    color: share.color,
    title: `${t(`cost.shares.${share.key}`)}: ${format.price(amount(share.key))}`,
  })),
)

const rows = computed(() => SHARES.filter((share) => props.fit.costShares[share.key] > 0))

const selected = computed(() => props.points.find((point) => point.step === props.step))

/** What the selected column means, in a sentence. */
const readout = computed(() => {
  const point = selected.value
  if (!point) return ''
  const cost = format.price(point.costPerPod * props.hours)
  if (point.step === 0) return t('cost.sensitivity.exact', { cost })
  const percent = format.percent(Math.abs(point.step) / 10)
  return t(point.step < 0 ? 'cost.sensitivity.lower' : 'cost.sensitivity.higher', { percent, cost })
})

const delta = computed(() => {
  const point = selected.value
  if (!point || point.step === 0) return null
  if (!Number.isFinite(point.costPerPod) || !Number.isFinite(props.fit.costPerPod)) return null
  const difference = (point.costPerPod - props.fit.costPerPod) * props.hours
  return t(difference <= 0 ? 'cost.sensitivity.saving' : 'cost.sensitivity.extra', {
    amount: format.price(Math.abs(difference)),
  })
})
</script>

<template>
  <section class="cost">
    <h4 class="cost__heading">
      {{ t('cost.heading') }}
      <InfoTip :label="t('cost.heading')" :text="t('cost.help')" />
    </h4>
    <StackedBar :segments="segments" />
    <dl class="cost__list">
      <div v-for="share in rows" :key="share.key" class="cost__row">
        <dt>
          <span
            class="cost__swatch"
            :class="{ 'cost__swatch--empty': share.color === 'none' }"
            :style="share.color === 'none' ? undefined : { background: `var(${share.color})` }"
            aria-hidden="true"
          />
          {{ t(`cost.shares.${share.key}`) }}
        </dt>
        <dd class="mono">{{ format.price(amount(share.key)) }}</dd>
      </div>
      <div class="cost__row cost__row--total">
        <dt>{{ t('cost.instance') }}</dt>
        <dd class="mono">{{ format.price(instanceCost) }}</dd>
      </div>
    </dl>

    <h4 class="cost__heading">
      {{ t('cost.sensitivity.heading') }}
      <InfoTip :label="t('cost.sensitivity.heading')" :text="t('cost.sensitivity.help')" />
    </h4>
    <SensitivityChart
      :points="points"
      :step="step"
      :hours="hours"
      @update:step="emit('update:step', $event)"
    />
    <p class="cost__readout" aria-live="polite">
      {{ readout }}
      <span v-if="delta" class="cost__delta">{{ delta }}</span>
    </p>
  </section>
</template>

<style scoped>
.cost {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.cost__heading {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: var(--space-2) 0 0;
  font-size: var(--text-sm);
  font-weight: 600;
}

.cost__list {
  margin: 0;
  font-size: var(--text-sm);
}

.cost__row {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-1) 0;
}

.cost__row + .cost__row {
  border-top: 1px solid var(--color-border);
}

.cost__row--total {
  font-weight: 600;
}

.cost__row dt {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.cost__row dd {
  margin: 0;
}

.cost__swatch {
  display: inline-block;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 2px;
}

.cost__swatch--empty {
  background: var(--color-surface-sunken);
  box-shadow: inset 0 0 0 1px var(--color-border-strong);
}

.cost__readout {
  margin: 0;
  font-size: var(--text-sm);
}

.cost__delta {
  display: block;
  color: var(--color-text-muted);
}
</style>
